import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, InputAdornment, Accordion, AccordionSummary,
  AccordionDetails, Switch, FormControlLabel, Slider, Select, MenuItem, FormControl,
  InputLabel, Button, Chip, Divider, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Tooltip, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StrategyEngineService from '../../../services/StrategyEngineService';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  'https://strategy-engine.houman-ghavamzadeh.workers.dev';

// Setting definitions with categories, descriptions, and impact labels
const SIMPLE_SETTINGS = [
  { key: 'maxConcurrentPositions', label: 'Max Concurrent Positions', type: 'number', category: 'risk', desc: 'Maximum number of open positions at once', impact: 'More positions = more exposure + more trades' },
  { key: 'sidePolicy', label: 'Buy/Sell Policy', type: 'select', options: [{ v: 'both', l: 'Both' }, { v: 'buy_only', l: 'Buy Only' }, { v: 'sell_only', l: 'Sell Only' }], category: 'risk', desc: 'Which trade directions are allowed', impact: 'Buy-only is safer in bullish markets' },
  { key: 'rescueScaleInEnabled', label: 'Rescue (Scale-In)', type: 'toggle', category: 'risk', desc: 'Average down on losing positions', impact: 'ON: can recover losers but increases exposure. OFF: cleaner exits.' },
  { key: 'scalpGateEnabled', label: 'Scalp Gate', type: 'toggle', category: 'signals', desc: 'Extra momentum/quality checks for scalp entries', impact: 'ON: fewer but higher-quality entries. OFF: more entries, lower avg quality.' },
  { key: 'microImpulseGateEnabled', label: 'Win Rate Boost Pack', type: 'toggle', category: 'signals', desc: 'Bundle of micro-impulse, fast-green, and anti-chop filters', impact: 'ON: higher win rate, fewer trades. OFF: more trades.' },
  { key: 'adaptiveProfitTrailEnabled', label: 'Adaptive Profit Trail', type: 'toggle', category: 'exits', desc: 'ATR-based trailing that adapts to volatility and leverage', impact: 'ON: exits adjust to market conditions. OFF: fixed trailing distance.' },
  { key: 'multiTargetTPEnabled', label: 'Multi-Target Take Profit', type: 'toggle', category: 'exits', desc: 'Partial exits at multiple R-multiple targets', impact: 'ON: lock in profits at 0.8R, 1.5R. OFF: single take-profit.' },
  { key: 'trailingStopFloorEnabled', label: 'Trailing Stop Floor', type: 'toggle', category: 'exits', desc: 'Minimum trailing distance that prevents exits too close to entry', impact: 'ON: prevents fee-losing exits. OFF: trailing can get very tight.' },
  { key: 'aiCloseGuardEnabled', label: 'AI Close Guard', type: 'toggle', category: 'exits', desc: 'Prevents AI-triggered exits in fee danger zone', impact: 'ON: no AI exits below breakeven. OFF: AI can close anytime.' },
  { key: 'zeroLockMeaningfulEnabled', label: 'Meaningful Zero-Lock', type: 'toggle', category: 'exits', desc: 'Requires sustained profit before activating zero-lock', impact: 'ON: fewer false zero-locks. OFF: locks at first touch of BE.' },
  { key: 'experienceModeEnabled', label: 'Experience Mode (ML)', type: 'toggle', category: 'experience', desc: 'Enable ML-driven optimization experiments', impact: 'ON: blocks weekend trading, enables combination cycling & suggestions. OFF: zero behavior change.' },
];

const ADVANCED_SETTINGS = [
  { key: 'confidenceThreshold', label: 'Confidence Threshold', type: 'number', category: 'signals', desc: 'Minimum signal confidence to enter' },
  { key: 'stopLossPercent', label: 'Stop Loss %', type: 'number', category: 'risk', desc: 'Maximum loss before position is closed' },
  { key: 'takeProfitPercent', label: 'Take Profit %', type: 'number', category: 'risk', desc: 'Target profit level' },
  { key: 'trailingStopPercent', label: 'Trailing Stop %', type: 'number', category: 'exits', desc: 'Distance of trailing stop from price' },
  { key: 'trailingBenefitActivation', label: 'Trailing Benefit Activation', type: 'number', category: 'exits', desc: 'Profit % to activate trailing benefit lock' },
  { key: 'trailingBenefitLockPercent', label: 'Trailing Benefit Lock %', type: 'number', category: 'exits', desc: 'Percentage of profit to lock when trailing' },
  { key: 'cooldownMinutes', label: 'Cooldown (minutes)', type: 'number', category: 'signals', desc: 'Min time between trades on same symbol' },
  { key: 'leverage', label: 'Leverage', type: 'number', category: 'risk', desc: 'Position leverage multiplier' },
  { key: 'positionSizePercent', label: 'Position Size %', type: 'number', category: 'risk', desc: 'Percentage of portfolio per trade' },
  { key: 'maxSignalsPerRun', label: 'Max Signals Per Run', type: 'number', category: 'signals', desc: 'Max entries per scan cycle' },
  { key: 'adaptiveProfitTrailATRMultiplier', label: 'Adaptive Trail ATR Multiplier', type: 'number', category: 'exits', desc: 'Multiplier for ATR-based trailing (0.3-1.0)' },
  { key: 'zeroLockSustainedTicks', label: 'Zero-Lock Sustained Ticks', type: 'number', category: 'exits', desc: 'Number of consecutive profit ticks before zero-lock' },
  { key: 'aiCloseGuardMinTPFraction', label: 'AI Guard Min TP Fraction', type: 'number', category: 'exits', desc: 'Minimum TP fraction before AI can close' },
  { key: 'strictEntryEnabled', label: 'Strict Entry Confirmation', type: 'toggle', category: 'signals', desc: 'Additional entry quality checks' },
  { key: 'enableVolumeControl', label: 'Volume Control', type: 'toggle', category: 'signals', desc: 'Volume-based entry filtering' },
  { key: 'enableCorrelation', label: 'Correlation Check', type: 'toggle', category: 'signals', desc: 'Cross-asset correlation filter' },
  { key: 'sellFilterStrict', label: 'Sell Filter Strict', type: 'toggle', category: 'signals', desc: 'Higher confidence required for sell entries' },
  { key: 'intentAutoDetectEnabled', label: 'Intent Auto-Detect', type: 'toggle', category: 'signals', desc: 'Derive scalp/trend intent from mode' },
  { key: 'experienceNoTradeWeekends', label: 'Block Weekend Trading', type: 'toggle', category: 'experience', desc: 'Block all entries on weekends when Experience Mode is ON' },
  { key: 'experienceAutoSuggestEnabled', label: 'Auto-Suggest', type: 'toggle', category: 'experience', desc: 'Automatically generate ML optimization suggestions' },
  { key: 'experienceAutoApplyEnabled', label: 'Auto-Apply Suggestions', type: 'toggle', category: 'experience', desc: 'Automatically apply ML suggestions (use with caution)' },
  { key: 'experienceMinTradesForDecision', label: 'Min Trades for Decision', type: 'number', category: 'experience', desc: 'Minimum trades before ML can make recommendations' },
  { key: 'experienceHoldoutPercent', label: 'Holdout %', type: 'number', category: 'experience', desc: 'Percentage of nodes kept as control group (0.0-1.0)' },
  { key: 'experienceAutoApplyMaxDelta', label: 'Max Auto-Apply Delta', type: 'number', category: 'experience', desc: 'Maximum setting change allowed per auto-apply (0.0-1.0)' },
];

const CATEGORY_LABELS = { risk: 'Risk', signals: 'Signals & Entry', exits: 'Exits & Trailing', experience: 'Experience Mode (ML)' };

function SettingRow({ setting, value, onChange, searchQuery }) {
  const theme = useTheme();
  const highlight = searchQuery && setting.label.toLowerCase().includes(searchQuery.toLowerCase());

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 1.5,
      borderRadius: 1,
      bgcolor: highlight ? alpha(theme.palette.warning.main, 0.08) : 'transparent',
      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
    }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" fontWeight={500}>{setting.label}</Typography>
          {setting.impact && (
            <Tooltip title={setting.impact} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">{setting.desc}</Typography>
      </Box>

      <Box sx={{ minWidth: 120 }}>
        {setting.type === 'toggle' ? (
          <Switch
            checked={value !== false && value !== 'false'}
            onChange={(e) => onChange(setting.key, e.target.checked)}
            size="small"
          />
        ) : setting.type === 'select' ? (
          <FormControl size="small" fullWidth>
            <Select value={value || setting.options[0]?.v} onChange={(e) => onChange(setting.key, e.target.value)} sx={{ fontSize: '0.8rem' }}>
              {setting.options.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </Select>
          </FormControl>
        ) : (
          <TextField
            value={value ?? ''}
            onChange={(e) => onChange(setting.key, e.target.value)}
            size="small"
            type="number"
            sx={{ width: 100, '& input': { fontSize: '0.8rem', py: 0.75 } }}
          />
        )}
      </Box>
    </Box>
  );
}

export default function SettingsTab({ strategyId, strategyData, scheduleData, modeInfo, strategies, onRefresh }) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [localSettings, setLocalSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [applyAllDialog, setApplyAllDialog] = useState(false);

  const effectiveSettings = useMemo(() => ({
    ...(scheduleData || {}),
    ...(strategyData?.settings || {}),
    ...localSettings,
  }), [scheduleData, strategyData, localSettings]);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const hasChanges = Object.keys(localSettings).length > 0;

  const handleSave = async () => {
    if (!strategyData) return;
    setSaving(true);
    try {
      await StrategyEngineService.updateStrategy(strategyId, {
        ...strategyData,
        settings: { ...strategyData.settings, ...localSettings },
      });
      setLocalSettings({});
      onRefresh?.();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToAll = async () => {
    setSaving(true);
    try {
      for (const s of strategies) {
        if (s.id === strategyId) continue;
        const sData = await StrategyEngineService.getStrategy(s.id);
        if (sData) {
          await StrategyEngineService.updateStrategy(s.id, {
            ...sData,
            settings: { ...sData.settings, ...localSettings },
          });
        }
      }
      setApplyAllDialog(false);
      setLocalSettings({});
      onRefresh?.();
    } catch (err) {
      console.error('Failed to apply to all:', err);
    } finally {
      setSaving(false);
    }
  };

  const filteredSimple = SIMPLE_SETTINGS.filter(s =>
    !searchQuery || s.label.toLowerCase().includes(searchQuery.toLowerCase()) || s.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAdvanced = ADVANCED_SETTINGS.filter(s =>
    !searchQuery || s.label.toLowerCase().includes(searchQuery.toLowerCase()) || s.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      {/* Search + Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search settings..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 250 }}
        />
        <Box sx={{ flex: 1 }} />
        {hasChanges && (
          <>
            <Chip label={`${Object.keys(localSettings).length} unsaved changes`} color="warning" size="small" variant="outlined" />
            <Button size="small" variant="outlined" color="inherit" onClick={() => setLocalSettings({})} startIcon={<RestoreIcon />} sx={{ textTransform: 'none' }}>
              Discard
            </Button>
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving} startIcon={<SaveIcon />} sx={{ textTransform: 'none' }}>
              Save
            </Button>
            <Button size="small" variant="outlined" color="primary" onClick={() => setApplyAllDialog(true)} startIcon={<GroupWorkIcon />} sx={{ textTransform: 'none' }}>
              Apply to All
            </Button>
          </>
        )}
      </Box>

      {/* Simple Settings */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold">Essential Settings</Typography>
          <Typography variant="caption" color="text.secondary">Key parameters that affect trading behavior</Typography>
        </Box>

        {Object.entries(CATEGORY_LABELS).map(([cat, catLabel]) => {
          const catSettings = filteredSimple.filter(s => s.category === cat);
          if (catSettings.length === 0) return null;
          return (
            <Box key={cat}>
              <Typography variant="caption" fontWeight={600} color="primary" sx={{ px: 1.5, pt: 1, display: 'block' }}>
                {catLabel}
              </Typography>
              {catSettings.map(setting => (
                <SettingRow
                  key={setting.key}
                  setting={setting}
                  value={effectiveSettings[setting.key]}
                  onChange={handleChange}
                  searchQuery={searchQuery}
                />
              ))}
              <Divider sx={{ mx: 1.5 }} />
            </Box>
          );
        })}
      </Paper>

      {/* Advanced Settings */}
      <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">Advanced Settings</Typography>
            <Typography variant="caption" color="text.secondary">Detailed parameters, thresholds, and gate controls</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {Object.entries(CATEGORY_LABELS).map(([cat, catLabel]) => {
            const catSettings = filteredAdvanced.filter(s => s.category === cat);
            if (catSettings.length === 0) return null;
            return (
              <Box key={cat}>
                <Typography variant="caption" fontWeight={600} color="primary" sx={{ px: 1.5, pt: 1, display: 'block' }}>
                  {catLabel}
                </Typography>
                {catSettings.map(setting => (
                  <SettingRow
                    key={setting.key}
                    setting={setting}
                    value={effectiveSettings[setting.key]}
                    onChange={handleChange}
                    searchQuery={searchQuery}
                  />
                ))}
                <Divider sx={{ mx: 1.5 }} />
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>

      {/* Apply-to-All Dialog */}
      <Dialog open={applyAllDialog} onClose={() => setApplyAllDialog(false)}>
        <DialogTitle>Apply to All Running Strategies?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will apply your {Object.keys(localSettings).length} changed setting(s) to all {strategies.length} strategies.
            Changes: {Object.entries(localSettings).map(([k, v]) => `${k}=${String(v)}`).join(', ')}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="caption">This action affects live trading. Ensure you've reviewed all changes.</Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyAllDialog(false)}>Cancel</Button>
          <Button onClick={handleApplyToAll} variant="contained" color="primary" disabled={saving}>
            {saving ? 'Applying...' : 'Apply to All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
